import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
} from "@mui/material";
import {
  Close as IconClose,
  AutoAwesome as AutoAwesomeIcon,
  Send as IconSend,
  Settings as SettingsIcon,
  ExpandLess as ExpandLessIcon,
  RestartAlt as RestartAltIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  getAllVoiceProfiles,
  ensureStarterModes,
  getCompanyKnowledge,
} from "../../utils/voiceProfileDB";
import { useWebLLM } from "../../contexts/WebLLMContext";
import { generateEmailWithWebLLM, parseGeneratedEmail } from "../../utils/assembleEmailPrompt";

/* ── Default prompts per writing mode ── */

const DEFAULT_PROMPTS = {
  mode_cold_outreach:
    "Write a professional cold outreach email. Introduce myself, reference my company's relevant offerings, and explain how I can provide value to them. Keep it concise and include a clear call to action.",
  mode_warm_followup:
    "Write a warm follow-up email referencing our previous interaction. Keep it friendly, mention how we can help, and suggest next steps.",
  mode_formal:
    "Write a formal, executive-level email. Use professional language, reference our company where relevant, and maintain a polished tone throughout.",
  mode_casual:
    "Write a casual, personal email. Keep it light, friendly, and conversational.",
  mode_thankyou:
    "Write a sincere thank-you email expressing gratitude. Keep it brief and genuine.",
};

const DEFAULT_PROMPT_GENERIC =
  "Write a professional email to this contact. Reference my company where relevant. Keep it clear and concise.";

function getPrimaryEmail(contact) {
  const reach = contact?.reach || {};
  const emails = Array.isArray(reach.emails) ? reach.emails : [];
  const found = emails.find((e) => String(e?.value || "").trim());
  return found?.value?.trim() || "";
}

function getContactDisplayName(contact) {
  const identity = contact?.identity || {};
  const person = identity.person || {};
  const firstName = (person.firstName || "").trim();
  const lastName = (person.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const email = getPrimaryEmail(contact);
  const displayName = fullName || identity.company?.name || "Recipient";
  return email ? `${displayName} <${email}>` : displayName;
}

function ComposeWithMagicDialog({ open, onClose, contact }) {
  const { engine, ready: llmReady, loading: llmLoading, downloading, progress, init: initLLM, resetChat } = useWebLLM();
  const user = useSelector((state) => state.auth.user);
  const senderFirstName = user?.firstName || user?.name?.split(" ")[0] || "";
  const [setupExpanded, setSetupExpanded] = useState(true);
  const [composedExpanded, setComposedExpanded] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState("not_submitted");
  const [writingModeId, setWritingModeId] = useState("");
  const [modes, setModes] = useState([]);
  const [companyKnowledge, setCompanyKnowledge] = useState("");
  const [tokenUsage, setTokenUsage] = useState(null);

  const contactEmail = getPrimaryEmail(contact);
  const hasEmail = Boolean(contactEmail);
  const hasComposedEmail = Boolean(subject.trim() || body.trim());

  /* ── Load writing modes + reset context on open ── */
  useEffect(() => {
    if (!open) return;
    initLLM();
    resetChat(); // clear previous conversation context
    let cancelled = false;
    (async () => {
      await ensureStarterModes();
      const [all, ck] = await Promise.all([getAllVoiceProfiles(), getCompanyKnowledge()]);
      if (!cancelled) {
        setModes(all);
        setCompanyKnowledge(ck);
      }
    })();
    return () => { cancelled = true; };
  }, [open, initLLM, resetChat]);

  useEffect(() => {
    if (!open) {
      setSetupExpanded(true);
      setComposedExpanded(true);
      setPrompt("");
      setSubject("");
      setBody("");
      setStatus("not_submitted");
      setWritingModeId("");
    }
  }, [open]);

  /* ── When writing mode changes, populate default prompt ── */
  const handleModeChange = (modeId) => {
    setWritingModeId(modeId);
    if (!modeId) return;
    const mode = modes.find((m) => m.id === modeId);
    const modePrompt = mode?.defaultPrompt?.trim();
    const fallback = DEFAULT_PROMPTS[modeId] || DEFAULT_PROMPT_GENERIC;
    setPrompt(modePrompt || fallback);
  };

  /* ── Compose with LLM ── */
  const handleCompose = useCallback(async () => {
    const selectedMode = modes.find((m) => m.id === writingModeId);
    if (!selectedMode || !prompt.trim() || !engine) return;

    setStatus("processing");
    setBody("");
    setSubject("");

    try {
      const result = await generateEmailWithWebLLM(
        engine,
        {
          voiceProfile: selectedMode,
          contact,
          purpose: prompt.trim(),
          companyKnowledge,
          senderFirstName,
        },
        (_delta, accumulated) => {
          const parsed = parseGeneratedEmail(accumulated);
          if (parsed.subject) setSubject(parsed.subject);
          setBody(parsed.body);
        },
      );

      // Final parse
      const { subject: finalSubject, body: finalBody } = parseGeneratedEmail(result.text);
      setSubject(finalSubject);
      setBody(finalBody);
      if (result.usage) setTokenUsage(result.usage);
      setStatus("completed");
    } catch (err) {
      console.error("Compose failed:", err);
      setStatus("error");
    }
  }, [engine, modes, writingModeId, prompt, contact]);

  const handleSendEmail = () => {
    if (!contactEmail || !hasComposedEmail) return;
    const mailtoSubject = encodeURIComponent(subject);
    const mailtoBody = encodeURIComponent(body);
    window.open(`mailto:${contactEmail}?subject=${mailtoSubject}&body=${mailtoBody}`, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          m: 1,
          width: { xs: "calc(100% - 8px)", sm: "calc(100% - 16px)" },
          height: "calc(100vh - 16px)",
          maxHeight: "calc(100vh - 16px)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.5, sm: 0.75 },
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
        <Typography sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: 700, color: "#7c3aed" }}>
          Compose with Magic
        </Typography>
        {status !== "not_submitted" && (
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: status === "completed" ? "#10b981" : status === "processing" ? "#f59e0b" : "#6b7280" }}>
            {status === "processing" ? "Processing..." : status === "completed" ? "Completed" : status}
          </Typography>
        )}
        {tokenUsage && (
          <Typography component="span" sx={{ fontSize: 10, color: tokenUsage.total_tokens > 24000 ? "#EF4444" : tokenUsage.total_tokens > 16000 ? "#F59E0B" : "#9CA3AF", fontWeight: 600 }}>
            {tokenUsage.total_tokens.toLocaleString()} / 32K tokens
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Box
          onClick={() => setSetupExpanded(!setupExpanded)}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer",
            px: 1, py: 0.25, borderRadius: 1,
            backgroundColor: setupExpanded ? "#F5F3FF" : "transparent",
            "&:hover": { backgroundColor: "#F5F3FF" },
          }}
          title={setupExpanded ? "Hide Setup" : "Show Setup"}
        >
          <SettingsIcon sx={{ fontSize: 14, color: setupExpanded ? "#7c3aed" : "#9CA3AF" }} />
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: setupExpanded ? "#7c3aed" : "#9CA3AF" }}>Setup</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <IconClose sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ p: { xs: 1, sm: 2 }, display: "flex", flexDirection: "column", overflow: "hidden", flex: 1, minHeight: 0, gap: { xs: 1, sm: 1.5 } }}>

        {/* ── Setup Card ── */}
        {setupExpanded && (
          <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 1.5, backgroundColor: "#FAFBFC", flexShrink: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <SettingsIcon sx={{ fontSize: 13, color: "#7c3aed" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Setup
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setSetupExpanded(false)}
                sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#64748b" } }}
              >
                <IconClose sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>

            {/* Writing Mode */}
            <Box sx={{ mb: 1.5 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={writingModeId}
                  onChange={(e) => handleModeChange(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: "#9CA3AF" }}>Select a writing mode</span>;
                    const mode = modes.find((m) => m.id === selected);
                    return mode?.name || selected;
                  }}
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: 13,
                    "& .MuiSelect-select": { py: 1 },
                  }}
                >
                  {(() => {
                    const customModes = modes.filter((m) => !m.isStarter);
                    const starterModes = modes.filter((m) => m.isStarter);
                    const items = [];
                    if (customModes.length > 0) {
                      items.push(
                        <ListSubheader key="hdr-custom" sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", lineHeight: "28px" }}>
                          My Modes
                        </ListSubheader>
                      );
                      customModes.forEach((m) =>
                        items.push(
                          <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
                            {m.name}
                          </MenuItem>
                        )
                      );
                    }
                    starterModes.forEach((m) =>
                      items.push(
                        <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
                          {m.name}
                        </MenuItem>
                      )
                    );
                    return items;
                  })()}
                </Select>
              </FormControl>
            </Box>

            {/* Prompt */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Write the message you want to turn into an email"
              InputLabelProps={{ shrink: true }}
              sx={{
                mb: 1,
                "& .MuiInputBase-root": { backgroundColor: "#ffffff", fontSize: "13px" },
                "& .MuiInputLabel-root": { fontSize: "12px" },
              }}
            />

            {/* Action buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!llmReady || status === "processing"}
                onClick={() => { resetChat(); setStatus("not_submitted"); setSubject(""); setBody(""); setTokenUsage(null); }}
                startIcon={<RestartAltIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: "none",
                  minWidth: 0,
                  px: 1.5,
                  height: 28,
                  color: "#6b7280",
                  borderColor: "#d1d5db",
                  "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" },
                }}
              >
                Clear Context
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!writingModeId || !prompt.trim() || status === "processing" || !llmReady}
                onClick={handleCompose}
                startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: "none",
                  minWidth: 0,
                  px: 2,
                  height: 28,
                  backgroundColor: "#7c3aed",
                  "&:hover": { backgroundColor: "#6d28d9" },
                  "&.Mui-disabled": { backgroundColor: "#e5e7eb", color: "#9ca3af" },
                }}
              >
                {llmLoading ? (downloading ? `Downloading model ${Math.round(progress * 100)}%` : "Loading model...") : "Compose with Magic"}
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Email Card ── */}
        <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 2, overflow: "hidden", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box
            onClick={() => setComposedExpanded(!composedExpanded)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
            }}
          >
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#1e293b" }}>Email</Typography>
            {composedExpanded ? <ExpandLessIcon sx={{ fontSize: 16, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
          </Box>

          {composedExpanded && (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto", p: 1.5 }}>
              {/* Recipient chip */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                <Chip
                  size="small"
                  label={
                    hasEmail
                      ? getContactDisplayName(contact)
                      : `${getContactDisplayName(contact)} • No email is added`
                  }
                  sx={
                    hasEmail
                      ? undefined
                      : {
                          color: "#B91C1C",
                          backgroundColor: "#FEE2E2",
                          border: "1px solid #FCA5A5",
                          fontWeight: 600,
                        }
                  }
                />
              </Box>

              {/* Subject + Send */}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "flex-start" }, gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  label="Subject line"
                  placeholder="Enter the email subject"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { backgroundColor: "#ffffff", fontSize: "13px" },
                    "& .MuiInputLabel-root": { fontSize: "12px" },
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSendEmail}
                  disabled={!hasComposedEmail || !hasEmail}
                  startIcon={<IconSend sx={{ fontSize: 14 }} />}
                  sx={{
                    height: 40,
                    minWidth: { xs: 0, sm: 150 },
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 700,
                    flexShrink: 0,
                    backgroundColor: hasComposedEmail ? "#16a34a" : "#e5e7eb",
                    color: hasComposedEmail ? "#fff" : "#9ca3af",
                    "&:hover": { backgroundColor: hasComposedEmail ? "#15803d" : "#d1d5db" },
                    "&.Mui-disabled": { backgroundColor: "#f3f4f6", color: "#9ca3af" },
                  }}
                >
                  Send email
                </Button>
              </Box>

              {/* Email body label */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                  Email Body
                </Typography>
              </Box>

              {/* Email body textarea */}
              <TextField
                fullWidth
                multiline
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Email body will appear here after execution..."
                sx={{
                  flex: 1,
                  display: "flex",
                  "& .MuiInputBase-root": {
                    backgroundColor: "#ffffff",
                    fontSize: "13px",
                    alignItems: "flex-start",
                    flex: 1,
                  },
                  "& .MuiInputBase-input": { flex: 1 },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default ComposeWithMagicDialog;
